import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Alert } from '../components/ui/alert';
import { EstadoError } from '../components/ui/estadoError';
import { sesionQueryOptions, useSesion } from '../features/auth/api/useSesion';
import { Inicio } from '../features/shell/components/inicio';

/**
 * `/` (T-3.1): igual que `_autenticado.tsx`, comprueba la sesión contra el servidor
 * (`ensureQueryData`, cachea entre rutas hermanas) antes de dejar pasar. Sin sesión → `/login`,
 * SIN `?redirect=` — a diferencia de `_autenticado.tsx`, aquí no hace falta guardar el destino
 * porque `/` ya es el destino por defecto tras el acceso. Con sesión, se ve `Inicio`.
 */
export const Route = createFileRoute('/')({
	beforeLoad: async ({ context }) => {
		try {
			await context.queryClient.ensureQueryData(sesionQueryOptions);
		} catch {
			throw redirect({ to: '/login' });
		}
	},
	component: PantallaInicio,
});

/**
 * Reparte la sesión ya resuelta por `beforeLoad` (la misma caché, sin repetir la petición) a
 * `Inicio`, el componente presentacional. `cargando`/`noSeConfirmo` son defensivos — con
 * `beforeLoad` ya en verde, `useSesion` debería devolver la sesión servida desde caché en el
 * primer render — pero cubren igualmente el caso límite en que la caché mute antes de este
 * render (p. ej. otra pestaña cerrando sesión, CL-4).
 */
function PantallaInicio(): React.JSX.Element {
	const { t } = useTranslation('shell');
	const { cargando, noSeConfirmo, sesion } = useSesion();

	if (cargando) {
		return <EstadoError mensaje={t('estados.cargando')} variante='carga' />;
	}

	if (noSeConfirmo || sesion === undefined) {
		return <Alert variante='error'>{t('estados.error')}</Alert>;
	}

	return <Inicio sesion={sesion} />;
}
